import React, { useEffect, useState, useCallback, useRef } from "react";
import OrderIndicator from "components/OrderIndicator";
import { useDispatch, useSelector } from "react-redux";
import { finalizePayouts, reset } from "redux/admin/payout/payoutSlice";
import { MdOutlineDone, MdClear, MdContentCopy, MdCheck } from "react-icons/md";
import { FaMoneyBill } from "react-icons/fa";
import { FiFilter, FiX, FiUser, FiCalendar, FiDollarSign } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import "moment/locale/tr";

moment.locale("tr");

const Payouts = ({ data, onFilterChange, filters, isLoading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    status: filters?.status || "",
    amount_min: filters?.amount_min || "",
    amount_max: filters?.amount_max || "",
    holder_name: filters?.holder_name || "",
  });
  const [copiedItems, setCopiedItems] = useState({});
  const debounceRef = useRef(null);

  const {
    isFinalizePayoutSuccess,
    isFinalizePayoutError,
    isFinalizePayoutLoading,
    message
  } = useSelector((state) => state.adminPayouts);

  // Update local filters when props change
  React.useEffect(() => {
    setLocalFilters({
      status: filters?.status || "",
      amount_min: filters?.amount_min || "",
      amount_max: filters?.amount_max || "",
      holder_name: filters?.holder_name || "",
    });
    
    // Auto-show filters if any filter is pre-filled
    const hasPrefilledFilters = Object.values(filters || {}).some(value =>
      value && value !== "" && value !== 1 && value !== 30
    );
    if (hasPrefilledFilters) {
      setShowFilters(true);
    }
  }, [filters]);

  const statusOptions = [
    { value: "pending_transfer", label: "Aktarım Bekliyor" },
    { value: "completed", label: "Aktarım Yapıldı" },
    { value: "failed", label: "Aktarım Reddedildi" },
  ];

  const handleFinalize = (id, status) => {
    dispatch(finalizePayouts({ payout_id: id, status: status }));
  };

  const handleCopy = (text, fieldName, payoutId) => {
    navigator.clipboard.writeText(text);
    const copyKey = `${payoutId}-${fieldName}`;
    setCopiedItems(prev => ({ ...prev, [copyKey]: true }));
    
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setCopiedItems(prev => {
        const newState = { ...prev };
        delete newState[copyKey];
        return newState;
      });
    }, 2000);
  };

  // Debounce function for automatic search
  const debouncedFilterChange = useCallback((newFilters) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      if (onFilterChange) {
        onFilterChange(newFilters);
      }
    }, 500); // 500ms delay
  }, [onFilterChange]);

  const handleFilterChange = (key, value) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
    
    // Trigger automatic search with debounce
    debouncedFilterChange(updatedFilters);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const clearFilters = () => {
    const emptyFilters = {
      status: "",
      amount_min: "",
      amount_max: "",
      holder_name: "",
    };
    setLocalFilters(emptyFilters);
    
    // Clear any pending debounced calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Immediately apply empty filters
    if (onFilterChange) {
      onFilterChange(emptyFilters);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("LLL");
  };

  // Check if any payout has TCKN data
  const hasTcknData = data?.some(payout => payout.tckn && payout.tckn.trim() !== '');

  // Format IBAN with spaces for valid IBANs
  const formatIban = (iban) => {
    if (!iban) return iban;
    
    // Basic IBAN validation - should start with 2 letters and be at least 15 chars
    const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    
    // Check if it's a valid IBAN format and reasonable length (15-34 chars)
    if (ibanRegex.test(cleanIban) && cleanIban.length >= 15 && cleanIban.length <= 34) {
      // Format with spaces every 4 characters
      return cleanIban.replace(/(.{4})/g, '$1 ').trim();
    }
    
    // Return original if not valid IBAN
    return iban;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <OrderIndicator color="green">Aktarım Yapıldı</OrderIndicator>;
      case "pending_transfer":
        return <OrderIndicator color="yellow">Aktarım Bekliyor</OrderIndicator>;
      case "failed":
        return <OrderIndicator color="red">Aktarım Reddedildi</OrderIndicator>;
      default:
        return <OrderIndicator color="red">Bilinmeyen Durum</OrderIndicator>;
    }
  };

  useEffect(() => {
    if (isFinalizePayoutSuccess) {
      toast.success("Talep Onaylandı");
      setTimeout(() => {
        // Reload the table data instead of the entire page
        if (onFilterChange) {
          onFilterChange(localFilters);
        }
      }, 1000);
    }
    if (isFinalizePayoutError) {
      toast.error("Bir Hata Oluştu: " + message.toString());
    }
    return () => {
      dispatch(reset());
    };
  }, [dispatch, isFinalizePayoutSuccess, isFinalizePayoutError, message, onFilterChange, localFilters]);

  return (
    <div className="overflow-x-auto p-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h3 className="text-xl font-semibold text-white-100">Ödeme Talepleri</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-primary text-white px-4 py-2 rounded-md font-semibold flex items-center gap-2 hover:bg-primary-dark transition-colors"
          >
            <FiFilter size={16} />
            Filtrele
          </button>
          {Object.values(localFilters).some(v => v) && (
            <button
              onClick={clearFilters}
              className="bg-red-500 text-white px-4 py-2 rounded-md font-semibold flex items-center gap-2 hover:bg-red-600 transition-colors"
            >
              <FiX size={16} />
              Temizle
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="bg-gray-800 p-4 mb-4 rounded-md">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-white-100 font-medium mb-1">Durum</label>
              <select
                value={localFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:outline-none"
              >
                <option value="">Tüm Durumlar</option>
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-white-100 font-medium mb-1">Min Tutar (TL)</label>
              <input
                type="number"
                value={localFilters.amount_min}
                onChange={(e) => handleFilterChange('amount_min', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:outline-none"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-white-100 font-medium mb-1">Max Tutar (TL)</label>
              <input
                type="number"
                value={localFilters.amount_max}
                onChange={(e) => handleFilterChange('amount_max', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:outline-none"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-white-100 font-medium mb-1">Ad Soyad Ara</label>
              <input
                type="text"
                value={localFilters.holder_name}
                onChange={(e) => handleFilterChange('holder_name', e.target.value)}
                className="w-full p-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:border-primary focus:outline-none"
                placeholder="Hesap sahibi adı..."
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile Card View */}
      <div className="block lg:hidden space-y-4">
        {isLoading ? (
          <div className="bg-gray-800 p-8 rounded-md text-center">
            <div className="text-white-100">Loading payouts...</div>
          </div>
        ) : (
          data?.map((payout) => (
            <div key={payout.id} className="bg-gray-800 p-4 rounded-md shadow-lg">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-2">
                <FiDollarSign className="text-primary" size={16} />
                <span className="text-lg font-semibold text-white-100">
                  {formatAmount(payout.amount_try)}
                </span>
              </div>
              <div className="text-xs text-gray-400">
                {formatDate(payout.created_at)}
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <FiUser className="text-gray-400" size={14} />
                <div>
                  <div className="text-white-100 font-medium">{payout.requestor.name}</div>
                  <div className="text-sm text-gray-400">Bayi</div>
                </div>
              </div>
              
              <div>
                <div className="text-gray-400 text-sm mb-1">Ad Soyad</div>
                <div className="flex items-center gap-2">
                  <span className="text-white-100">{payout.holder_name}</span>
                  {copiedItems[`${payout.id}-Ad Soyad`] ? (
                    <MdCheck
                      className="text-green-500 transition-colors"
                      size={26}
                    />
                  ) : (
                    <MdContentCopy
                      className="cursor-pointer text-primary hover:text-primary-dark transition-colors"
                      onClick={() => handleCopy(payout.holder_name, "Ad Soyad", payout.id)}
                      size={26}
                    />
                  )}
                </div>
              </div>
              
              <div>
                <div className="text-gray-400 text-sm mb-1">İban</div>
                <div className="flex items-center gap-2">
                  <span className="text-white-100 font-mono text-sm">{formatIban(payout.iban)}</span>
                  {copiedItems[`${payout.id}-İban`] ? (
                    <MdCheck
                      className="text-green-500 transition-colors"
                      size={26}
                    />
                  ) : (
                    <MdContentCopy
                      className="cursor-pointer text-primary hover:text-primary-dark transition-colors"
                      onClick={() => handleCopy(payout.iban, "İban", payout.id)}
                      size={26}
                    />
                  )}
                </div>
              </div>
              
              {hasTcknData && (
                <div>
                  <div className="text-gray-400 text-sm mb-1">TCKN</div>
                  <div className="text-white-100 font-mono text-sm">{payout.tckn}</div>
                </div>
              )}
              
              <div>
                <div className="text-gray-400 text-sm mb-1">Komisyon</div>
                <div className="text-white-100">{formatAmount(payout.fee_amount)}</div>
              </div>
              
              <div>
                <div className="text-gray-400 text-sm mb-1">Durum</div>
                {getStatusBadge(payout.status)}
              </div>
              
              {payout.transfer_reference && (
                <div>
                  <div className="text-gray-400 text-sm mb-1">Referans</div>
                  <div className="text-white-100 font-mono text-sm">{payout.transfer_reference}</div>
                </div>
              )}
            </div>
            
            {payout.status === "pending_transfer" && (
              <div className="flex gap-2 mt-4 pt-3 border-t border-gray-700">
                <button
                  onClick={() => handleFinalize(payout.id, "completed")}
                  className="bg-green-600 text-white rounded-md px-3 py-2 flex items-center gap-2 text-sm font-semibold hover:bg-green-700 transition-colors"
                  disabled={isFinalizePayoutLoading}
                >
                  <MdOutlineDone size={16} />
                  Onayla
                </button>
                <button
                  onClick={() => handleFinalize(payout.id, "failed")}
                  className="bg-red-500 text-white rounded-md px-3 py-2 flex items-center gap-2 text-sm font-semibold hover:bg-red-600 transition-colors"
                  disabled={isFinalizePayoutLoading}
                >
                  <MdClear size={16} />
                  Reddet
                </button>
                <button
                  onClick={() => handleFinalize(payout.id, "send_bank")}
                  className="bg-primary text-white rounded-md px-3 py-2 flex items-center gap-2 text-sm font-semibold hover:bg-primary-dark transition-colors"
                  disabled={isFinalizePayoutLoading}
                >
                  <FaMoneyBill size={14} />
                  Kuveyt Gönder
                </button>
              </div>
            )}
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block">
        <table className="min-w-full divide-y-2 divide-gray-700 text-sm bg-gray-900 rounded-lg overflow-hidden">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-white-100 font-semibold">Tutar</th>
              <th className="px-4 py-3 text-left text-white-100 font-semibold">Komisyon</th>
              <th className="px-4 py-3 text-left text-white-100 font-semibold">Bayi Adı</th>
              <th className="px-4 py-3 text-left text-white-100 font-semibold">Ad Soyad</th>
              <th className="px-4 py-3 text-left text-white-100 font-semibold">İban</th>
              {hasTcknData && (
                <th className="px-4 py-3 text-left text-white-100 font-semibold">TCKN</th>
              )}
              <th className="px-4 py-3 text-left text-white-100 font-semibold">İşlem Tarihi</th>
              <th className="px-4 py-3 text-left text-white-100 font-semibold">Durum</th>
              <th className="px-4 py-3 text-left text-white-100 font-semibold">Referans</th>
              <th className="px-4 py-3 text-left text-white-100 font-semibold">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {isLoading ? (
              <tr>
                <td colSpan={hasTcknData ? "10" : "9"} className="px-4 py-8 text-center text-white-100">
                  Loading payouts...
                </td>
              </tr>
            ) : (
              data?.map((payout) => (
                <tr key={payout.id} className="hover:bg-gray-800 transition-colors">
                  <td className="px-4 py-3 font-semibold text-white-100">
                    {formatAmount(payout.amount_try)}
                  </td>
                  <td className="px-4 py-3 font-medium text-white-100">
                    {formatAmount(payout.fee_amount)}
                  </td>
                  <td className="px-4 py-3 text-white-100">
                    {payout.requestor.name}
                  </td>
                  <td className="px-4 py-3 text-white-100">
                    <div className="flex items-center gap-2">
                      {payout.holder_name}
                      {copiedItems[`${payout.id}-Ad Soyad`] ? (
                        <MdCheck
                          className="text-green-500 transition-colors"
                          size={20}
                        />
                      ) : (
                        <MdContentCopy
                          className="cursor-pointer text-primary hover:text-primary-dark transition-colors"
                          onClick={() => handleCopy(payout.holder_name, "Ad Soyad", payout.id)}
                          size={20}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white-100">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{formatIban(payout.iban)}</span>
                      {copiedItems[`${payout.id}-İban`] ? (
                        <MdCheck
                          className="text-green-500 transition-colors"
                          size={20}
                        />
                      ) : (
                        <MdContentCopy
                          className="cursor-pointer text-primary hover:text-primary-dark transition-colors"
                          onClick={() => handleCopy(payout.iban, "İban", payout.id)}
                          size={20}
                        />
                      )}
                    </div>
                  </td>
                  {hasTcknData && (
                    <td className="px-4 py-3 text-white-100 font-mono text-sm">
                      {payout.tckn}
                    </td>
                  )}
                  <td className="px-4 py-3 text-white-100 text-xs">
                    {formatDate(payout.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(payout.status)}
                  </td>
                  <td className="px-4 py-3 text-white-100 font-mono text-xs">
                    {payout.transfer_reference || '-'}
                  </td>
                  <td className="px-4 py-3">
                    {payout.status === "pending_transfer" ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleFinalize(payout.id, "completed")}
                          className="bg-green-600 text-white rounded-md p-2 flex items-center font-semibold cursor-pointer hover:bg-green-700 transition-colors"
                          disabled={isFinalizePayoutLoading}
                          title="Onayla"
                        >
                          <MdOutlineDone size={16} />
                        </button>
                        <button
                          onClick={() => handleFinalize(payout.id, "failed")}
                          className="bg-red-500 text-white rounded-md p-2 flex items-center font-semibold cursor-pointer hover:bg-red-600 transition-colors"
                          disabled={isFinalizePayoutLoading}
                          title="Reddet"
                        >
                          <MdClear size={16} />
                        </button>
                        <button
                          onClick={() => handleFinalize(payout.id, "send_bank")}
                          className="bg-primary text-white rounded-md px-3 py-2 flex items-center gap-1 font-semibold cursor-pointer hover:bg-primary-dark transition-colors"
                          disabled={isFinalizePayoutLoading}
                          title="Kuveyt Gönder"
                        >
                          <FaMoneyBill size={14} />
                          <span className="text-xs">Kuveyt</span>
                        </button>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(!data || data.length === 0) && !isLoading && (
        <div className="text-center py-8 text-gray-400">
          <FiDollarSign size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg">Ödeme talebi bulunamadı.</p>
        </div>
      )}
    </div>
  );
};

export default Payouts;
